import {schema} from '@/common/spec/spec-cache';
import capitalize from '@/common/helpers/capitalize';

export default class SpecResolver {
    /**
     * Reference schema and allow for external overriding
     */
    static schema = schema;

    /**
     * Cache of resolved entities
     * @type {{}}
     */
    static resolveCache = {};

    /**
     * Path delimiter in $ref values
     * @type {string}
     */
    static pathDelimiter = '/';

    /**
     * Properties to skip when parsing objects
     * @type {string[]}
     */
    static propertyExclusions = ['_links', '_embedded', 'customFields'];

    /**
     * Resolve an entity within the API spec and dereference all its properties recursively.
     * If timing is enabled the execution time will be printed.
     * @param entityName {string}
     * @param timing {boolean}
     * @return {object}
     */
    static resolveEntity(entityName, {timing = false} = {}) {
        const timeLabel = `Resolve "${entityName}" spec`;
        if (timing) {
            console.time(timeLabel);
        }
        if (SpecResolver.schema[entityName] === undefined) {
            console.error(`Unable to find "${entityName}" in the spec. Did you mean "${capitalize(entityName)}"?`);
            return {};
        }
        if (SpecResolver.resolveCache[entityName] === undefined) {
            SpecResolver.resolveCache[entityName] = SpecResolver
                .resolveRecursive({...SpecResolver.schema[entityName]}, entityName);
        }
        if (timing) {
            console.timeEnd(timeLabel);
        }
        return SpecResolver.resolveCache[entityName];
    }

    /**
     * Recursively resolve entities
     * @param definition {object}
     * @param parentEntityName {string|null}
     * @return {*}
     */
    static resolveRecursive(definition, parentEntityName = null) {
        // resolve with discriminator
        if (parentEntityName && definition.discriminator) {
            // when a discriminator is present we need to verify
            // that all values enumerated exist as entities
            const {propertyName} = definition.discriminator;
            if (definition.properties
                && definition.properties[propertyName]
                && definition.properties[propertyName].enum) {
                const parts = definition.properties[propertyName].enum;
                const isParentRef = section => section.$ref
                    && parentEntityName === SpecResolver.getRefEntityName(section.$ref);
                const predicate = part => SpecResolver.schema[part] !== undefined
                    && SpecResolver.schema[part].allOf
                    && SpecResolver.schema[part].allOf.find(isParentRef) !== undefined;
                // check if every enum value matches the predicate
                if (parts.every(predicate)) {
                    const combined = parts.reduce((result, part) => {
                        // filter out $ref objects because
                        // we consider them circular references to parent objects
                        const subSchema = [...SpecResolver.schema[part].allOf.filter(p => !p.$ref)];
                        return SpecResolver.mergeCombined(
                            result,
                            SpecResolver.resolveRecursive({allOf: subSchema}),
                        );
                    }, {});
                    const merged = {
                        ...SpecResolver.resolveRecursive(definition),
                        ...combined,
                    };
                    Reflect.deleteProperty(merged, '$nested');
                    return merged;
                }
            }
        }
        // the object references multiple sub-entities or schemas
        if (definition.allOf) {
            const base = {};
            if (definition.description) {
                // keep original description
                base.$description = definition.description;
            }
            return definition.allOf
                .reduce((combined, part) => (
                    {
                        ...combined,
                        ...SpecResolver.resolveRecursive(part),
                    }
                ), base);
        }
        // we have a single $ref path
        if (definition.$ref) {
            const entityName = SpecResolver.getRefEntityName(definition.$ref);
            return SpecResolver.setNestedFlag(SpecResolver.resolveEntity(entityName));
        }
        // the object defines properties and we resolve them in the current node
        if (definition.properties && (definition.type === 'object' || definition.type === undefined)) {
            const resolved = Object.keys(definition.properties)
            // check for excluded properties
                .filter(p => !SpecResolver.propertyExclusions.includes(p))
                .reduce((combined, property) => ({
                    ...combined,
                    [property]: SpecResolver.resolveRecursive(definition.properties[property]),
                }), {});
            if (definition.properties.customFields) {
                // set flag for custom fields as they are found in the API instead of the spec
                resolved.$customFields = true;
            }
            // if we're not at the top level check if a $nested flag is required
            return parentEntityName === null
                ? SpecResolver.setNestedFlag(resolved)
                : resolved;
        }
        // if we have a normal property we copy it over
        if (definition.type !== 'array') {
            return {...definition};
        }
        // if we have an array defining items we need to determine what to do
        if (definition.type === 'array' && definition.items) {
            // if there's a type within the items
            // then we have either an inline definition of properties
            // or a set of references
            if (definition.items.type) {
                // check if the properties are defined inline
                if (definition.items.properties) {
                    return SpecResolver.setNestedFlag({
                        ...SpecResolver.resolveRecursive(definition.items),
                        $array: true,
                    });
                }
                // otherwise merge the `items` definition inline
                // and add a flag
                const resolved = {
                    ...definition,
                    $array: true,
                };
                Reflect.deleteProperty(resolved, 'items');
                const item = {...definition.items};
                Reflect.deleteProperty(item, 'type');
                if (item.description) {
                    item.$description = item.description;
                    Reflect.deleteProperty(item, 'description');
                }
                return {...resolved, ...item};
            }
            // if there's no type process references and combine them
            const resolvedItems = SpecResolver.resolveRecursive(definition.items);
            return SpecResolver.setNestedFlag({
                ...resolvedItems,
                // flag for accessor paths requiring `object.N.prop` notation
                $array: true,
            });
        }
        return {};
    }

    /**
     * Merge two objects while combining array results when the keys collide.
     * Otherwise the `to` property is used.
     * @param from {object}
     * @param to {object}
     * @return {object}
     */
    static mergeCombined(from, to) {
        const copy = {...from};
        Object.keys(to).forEach((key) => {
            // check if the key exists in both objects
            if (from[key] !== undefined) {
                copy[key] = SpecResolver.copyProp(from[key]);
                Object.keys(to[key]).forEach((propKey) => {
                    // check if both props are arrays
                    if (copy[key][propKey] !== undefined
                        && Array.isArray(copy[key][propKey])
                        && Array.isArray(to[key][propKey])) {
                        // merge the arrays while
                        // keeping only unique values
                        copy[key][propKey] = [...new Set([
                            ...copy[key][propKey],
                            ...to[key][propKey],
                        ])];
                        // leave behind a flag
                        copy[key].$merged = true;
                    } else {
                        if (!copy[key]) {
                            copy[key] = {};
                        }
                        copy[key][propKey] = to[key][propKey];
                    }
                });
            } else {
                copy[key] = SpecResolver.copyProp(to[key]);
            }
        });
        return copy;
    }

    /**
     * Set a $nested flag if the definition does not have a type property
     * @param definition
     * @return {object}
     */
    static setNestedFlag(definition) {
        if (definition.type === undefined || typeof definition.type !== 'string') {
            definition.$nested = true;
        }
        return definition;
    }

    /**
     * Copy a property as an object or a primitive
     * @param prop
     * @return {{}}
     */
    static copyProp(prop) {
        return typeof prop === 'object'
            ? {...prop}
            : prop;
    }

    /**
     * Get the entity linked in the reference path
     * @param $ref {string}
     * @return {string}
     */
    static getRefEntityName($ref) {
        return $ref.split(SpecResolver.pathDelimiter).pop();
    }
}
